import { useCallback, useEffect, useMemo } from 'react';
import { PageLoading } from '../../../../components/page';
import { Slides } from '../../../../components/slides/Slides';
import { AssessmentForm } from '../../../../content/assessment/components/AssessmentForm';
import { AssessmentContinueArgs } from '../../../../content/assessment/components/AssessmentSlideTypes';
import { SurveyQuestionnaireQuery } from '../../../../graphQL';
import { getRoute, useNavigate } from '../../../../routes';
import { useSurveyContext } from '../contexts/surveyContext';
import {
  SurveySlide,
  SurveySlideProps,
  useSurveySlideContext,
} from '../contexts/surveySlideContext';
import { useSurveyNavigation } from '../hooks/useSurveyNavigation';
import { SurveyQuestionnaireRoute } from '../surveyRoutes';

type SurveySlidesProps = {
  questionnaireRoute: SurveyQuestionnaireRoute;
  questionnaire: SurveyQuestionnaireQuery['surveyQuestionnaire'];
};

export const SurveyQuestionSlides = ({
  questionnaireRoute,
  questionnaire,
}: SurveySlidesProps): JSX.Element => {
  const navigate = useNavigate();
  const { setAnswer } = useSurveyContext();
  const slideContext = useSurveySlideContext();
  const navigation = useSurveyNavigation();

  const { questions, slides } = useMemo((): {
    questions: typeof flatQuestions;
    slides: SurveySlide[];
  } => {
    if (questionnaire?.questions === undefined) {
      return { questions: [], slides: [] };
    }

    const flatQuestions = questionnaire?.questions.map(question => ({
      ...question,
      questionnaireKey: questionnaire.key,
    }));

    const questionSlides = flatQuestions?.map(() => {
      return (props: SurveySlideProps) => (
        <AssessmentForm
          {...props}
          buttonTextOverride="Next"
          buttonAriaLabelOverride="Proceed to next step of assessment"
          showMonitorWarning={false}
        />
      );
    });

    // Do final slide in the map instead of hacking here!
    return {
      questions: flatQuestions,
      slides: [...(questionSlides ?? [])],
    };
  }, [questionnaire]);

  const { nextSlide, registerNavigation, registerSlides, triggerNextSlide } = slideContext;

  // Stabilize the navigation function to prevent unnecessary re-registrations
  const navigationHandler = useCallback(
    (newSlideNumber: string): void => {
      navigate(
        getRoute('surveyAssessments', {
          questionnaireRoute,
          slide: newSlideNumber,
        }),
      );
    },
    [navigate, questionnaireRoute],
  );

  useEffect(() => {
    if (slides === undefined || slides.length === 0) {
      return;
    }

    registerNavigation(navigationHandler);
    registerSlides(slides);

    // The slides have loaded so take the user to the first slide.
    // This is to prevent incomplete assessments from being submitted.
    navigate(getRoute('surveyAssessments', { questionnaireRoute, slide: '1' }));

    // Only depend on slides and questionnaireRoute to prevent infinite re-registrations
    // The context functions (registerNavigation, registerSlides) shouldn't change,
    // and navigationHandler is memoized with useCallback
  }, [slides, questionnaireRoute]);

  // Function for stepping between slides and handling answers.
  const onContinue = ({ question, answer }: AssessmentContinueArgs): void => {
    setAnswer(question.questionnaireKey, question.key, answer);

    if (nextSlide) {
      triggerNextSlide();
    } else {
      // Use survey navigation to go to next step in flow (or results if no next step)
      navigation.goToNext();
    }
  };

  if (questions.length === 0) {
    return <PageLoading pageName="Student Survey" />;
  }

  return <Slides context={slideContext} questions={questions} onContinue={onContinue} />;
};
